import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { MdOutlineMailOutline } from "react-icons/md";
import { RiLockPasswordLine } from "react-icons/ri";
import CustomLogSignInput from "../../../UI/CustomLogSignInput";
import AuthShell, { linkSx, primaryButtonSx } from "../../../Hooks/store/AuthShell";
import { useAuthStore } from "../../../Hooks/store/useAuthStore";
import { tokens } from "../../../Hooks/store/tokens";

const AUTH_PATHS = ["/login", "/signup", "/register", "/forgetpass"];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Where to go after login: the page the visitor was trying to open,
  // unless that is itself an auth page (avoids redirect loops).
  // const from = location.state?.from;
  // const redirectUi =
  //   from && !AUTH_PATHS.includes(from.split("?")[0].toLowerCase()) ? from : "/";

  const rawFrom = location.state?.from;

const from =
  typeof rawFrom === "string"
    ? rawFrom
    : rawFrom?.pathname
      ? `${rawFrom.pathname}${rawFrom.search || ""}${rawFrom.hash || ""}`
      : "/";

const redirectUi = !AUTH_PATHS.includes(
  from.split("?")[0].toLowerCase()
)
  ? from
  : "/";



  const notice = location.state?.notice;

  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const isLoggingIn = useAuthStore((s) => s.isLoggingIn);
  const rememberedEmail = useAuthStore((s) => s.rememberedEmail);
  const fetchIp = useAuthStore((s) => s.fetchIp);
  const hydrateFromCookie = useAuthStore((s) => s.hydrateFromCookie);

  const [rememberMe, setRememberMe] = useState(!!rememberedEmail);

  const initialIdentifier = rememberedEmail || location.state?.identifier || "";
  const { control, handleSubmit } = useForm({
    defaultValues: { MobileNumber: initialIdentifier, pass: "" },
  });

  useEffect(() => {
    fetchIp();
  }, [fetchIp]);

  // Already signed in (zustand/localStorage) or signed in on the main
  // domain (SSO cookie)? Skip the form.
  useEffect(() => {
    if (user?.sopnoid || hydrateFromCookie()) {
      navigate(redirectUi, { replace: true });
    }
  }, [user?.sopnoid, hydrateFromCookie, navigate, redirectUi]);

  // On success the store sets the session; the effect above navigates.
  const onSubmit = ({ MobileNumber, pass }) =>
    login({ email: MobileNumber.trim(), password: pass, rememberMe });

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to continue to Songjukti"
      footer={
        <Typography variant="body2" sx={{ color: tokens.color.inkMuted }}>
          Don't have an account?{" "}
          <Typography component={Link} to="/signup" variant="body2" sx={linkSx}>
            Create one
          </Typography>
        </Typography>
      }
    >
      <Stack
        component="form"
        noValidate
        autoComplete="off"
        spacing={2}
        onSubmit={handleSubmit(onSubmit)}
      >
        {notice && <Alert severity="success">{notice}</Alert>}

        <CustomLogSignInput
          control={control}
          name="MobileNumber"
          placeholder="Email or phone"
          icon={MdOutlineMailOutline}
          rules={{ required: "This field is required" }}
          defaultValue={initialIdentifier}
          type="text"
          fontSize={14}
          iconSize={20}
          height={44}
          setValue={() => {}}
        />

        <CustomLogSignInput
          control={control}
          name="pass"
          placeholder="Password"
          icon={RiLockPasswordLine}
          rules={{ required: "This field is required" }}
          defaultValue=""
          type="password"
          fontSize={14}
          iconSize={20}
          height={44}
          setValue={() => {}}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                sx={{
                  color: tokens.color.inkFaint,
                  "&.Mui-checked": { color: tokens.color.teal },
                  "& .MuiSvgIcon-root": { fontSize: 20 },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: tokens.color.ink }}>
                Remember my email
              </Typography>
            }
          />
          <Typography component={Link} to="/forgetpass" variant="body2" sx={linkSx}>
            Forgot password?
          </Typography>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoggingIn}
          sx={primaryButtonSx}
        >
          {isLoggingIn ? (
            <CircularProgress size={22} sx={{ color: tokens.color.white }} />
          ) : (
            "Log in"
          )}
        </Button>
      </Stack>
    </AuthShell>
  );
};

export default React.memo(Login);